<template>
  <v-content>
    <v-container fluid>
      <v-row>
        <v-col cols="12" lg="3" md="5" sm="5">
          <v-row
            no-gutters
            justify="space-between"
            align="start"
            class="mb-2 mx-1"
          >
            <v-card outlined class="mx-auto" max-width="400" width="100%">
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="overline mb-4">{{ $t("data.data.device") }}</div>
                  <v-list-item-title class="headline mb-1">
                    <device-selection></device-selection>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-row>
          <v-row no-gutters class="mb-2" v-if="sensorList.length !== 0">
            <v-card class="mx-auto" max-width="400" width="100%" outlined>
              <v-list-item three-line>
                <v-list-item-content>
                  <div class="overline mb-4">{{ $t("data.data.sensors") }}</div>
                  <v-list-item-title class="headline mb-1">
                    <sensor-picker
                      ref="sensorPicker"
                      :sensor-list="sensorList"
                      :location="location"
                      :selected-sensors="selectedSensors"
                      @selected-sensors-data="getSelectedSensors"
                      @fetch-sensor-data="loadSensorData"
                      :key="forceUpdateSensorPicker"
                    ></sensor-picker>
                    <!-- :key="forceRefresh" -->
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </v-row>
          <v-row no-gutters class="hidden-lg-and-up">
            <data-alerts :selectedAlerts="alerts"></data-alerts>
          </v-row>
        </v-col>

        <v-col cols="12" lg="6" md="7" sm="7">
          <v-row no-gutters justify="center" align="start" v-if="deviceSerial">
            <v-card class="mx-auto" width="100%" color="primary">
              <chart
                :selected-sensors="selectedSensors"
                :location="location"
                :selected-alerts="selectedAlerts"
                :key="forceRefreshCharts"
                :date-interval="dateRange"
                @set-hovered-data="hoveredData = $event"
              ></chart>
              <v-overlay absolute :value="loading || loadingCharts">
                <v-progress-circular
                  indeterminate
                  size="64"
                ></v-progress-circular>
              </v-overlay>
            </v-card>
          </v-row>
          <v-row
            no-gutters
            justify="space-between"
            align="start"
            v-if="deviceSerial && showChart"
          >
            <v-card class="mx-auto" width="100%" outlined :loading="loading">
              <v-overlay :value="overlay" absolute>
                <v-btn icon>
                  {{ $t("data.data.noDataFoundForThisDateRange") }}
                </v-btn>
              </v-overlay>
              <travel-map
                :set-hovered-data="hoveredData"
                :location="location"
                :key="forceRefresh"
              ></travel-map>
            </v-card>
          </v-row>
        </v-col>
        <v-col cols="3" lg="3" md="3" sm="5" class="hidden-md-and-down">
          <data-alerts :selectedAlerts="alerts"></data-alerts>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import DeviceSelection from "@/components/selectors/DeviceSelection";
import SensorPicker from "@/components/pickers/SensorPicker";
import api from "@/utils/api";
import Chart from "@/components/miscellaneous/Chart";
import travelMap from "@/components/maps/TravelMap";
import DataAlerts from "@/components/data/DataAlerts";
import snackbar from "@/utils/snackbar";
import unitConverter from "@/utils/unitConverter";
import numbro from "numbro";
import { mapGetters } from "vuex";

export default {
  components: {
    DeviceSelection,
    SensorPicker,
    Chart,
    travelMap,
    DataAlerts,
  },
  data: () => ({
    dateRange: [],
    deviceSerial: null,
    alerts: [],
    selectedAlerts: [],
    sensorList: [],
    location: [],
    loadingCharts: false,
    loading: true,
    overlay: false,
    showChart: true,
    hoveredData: null,
    selectedSensors: { selected: [], data: [] },
    forceRefresh: 0,
    forceRefreshCharts: 0,
    forceUpdateSensorPicker: 0,
  }),
  created() {
    this.handleRoute(this.$route);
  },

  watch: {
    $route(newValue, oldValue) {
      if (oldValue.query.id !== newValue.query.id) {
        this.loadData(false);
      } else if (
        oldValue.query.datefrom !== newValue.query.datefrom ||
        oldValue.query.dateto !== newValue.query.dateto
      ) {
        this.loadData(true);
      }
    },
  },

  computed: {
    ...mapGetters(["getMe"]),
  },
  methods: {
    setIntialData() {
      this.dateRange = [
        this.$route.query.datefrom,
        this.$moment(this.$route.query.dateto).add(1, "days"),
      ].map((x) => this.$moment(x).format());

      this.deviceSerial = this.$route.query.id;
    },

    loadData(status) {
      if (
        typeof this.$route.query.id === "undefined" ||
        this.$route.query.id === null
      ) {
        this.alerts = [];
        this.selectedAlerts = [];
        this.sensorList = [];
        this.location = [];
        this.deviceSerial = null;
        return;
      }

      this.setIntialData();

      if (!status) {
        this.selectedSensors = { selected: [], data: [] };
      }

      this.loading = true;
      const sensorList = this.loadSensorList();
      const location = this.loadLocation();
      const alerts = this.loadAlerts();

      let handleAlerts = (value) => {
        if (value.status) {
          value.data.forEach((element) => {
            element.created = this.$moment(element.created).unix();
          });
          value.data.sort((a, b) => {
            return b.created - a.created;
          });
        }

        if (!status) {
          this.alerts = value.data;
        }

        this.$set(this.selectedAlerts);
        this.selectedAlerts = value.data;
      };

      let handleSensorList = (value) => {
        let loadSensors = (sensorsToLoad) => {
          let promises = sensorsToLoad.map((element) => {
            return new Promise((resolve, reject) => {
              setTimeout(() => {
                let sensor = this.$refs.sensorPicker.getSensor(element);
                this.$refs.sensorPicker.deleteSensorData(sensor);
                this.loadSensorData(sensor.uuid, function (callback) {
                  resolve({ sensor: sensor, data: callback.data });
                });
              }, 0);
            });
          });
          Promise.all(promises).then((val) => {
            this.$refs.sensorPicker.setSensorData(val);
            this.loadingCharts = false;
          });
        };

        this.loadingCharts = true;
        if (!status) {
          //GET USERS unitPreference FROM USERS/CRM/ME
          const unitPreference = this.getMe.user.profile.unit_preference;
          value.data.sensors = value.data.sensors.flatMap((data) => {
            return {
              ...data,
              last_record_value: unitConverter.format(
                data.last_record_value,
                data.sensor.measure_type,
                unitPreference,
                null,
                null,
                false
              ),
            };
          });

          this.sensorList = value.data;
          let sensorsToLoad = this.getSelectedSensorsFromLink().filter(
            (sensor) => {
              return !this.selectedSensors.selected.includes(sensor);
            }
          );
          loadSensors(sensorsToLoad);
        } else {
          loadSensors(this.getSelectedSensorsFromLink());
        }
      };

      let handleLocation = (value) => {
        if (value.data.length !== 0) {
          this.overlay = false;
          value.data.forEach((element) => {
            element.created = this.$moment(element.created).unix();
            element.position = {
              lat: element.position.latitude,
              lng: element.position.longitude,
            };
          });
          let filteredLocations = value.data.filter(
            (item) =>
              typeof item.position !== "undefined" &&
              item.position.lat !== 0 &&
              item.position.lng !== 0
          );
          if (filteredLocations.length === 0) {
            this.location = value.data;
          } else {
            this.location = filteredLocations;
          }
        } else {
          this.location = [];
          this.overlay = true;
        }
      };

      Promise.allSettled([sensorList, location, alerts]).then((values) => {
        let text = [];

        if (values[0].status !== "rejected") {
          handleSensorList(values[0].value);
        } else {
          this.sensorList = [];
          text.push("sensors");
        }

        if (values[1].status !== "rejected") {
          this.showChart = true;
          handleLocation(values[1].value);
        } else {
          this.showChart = false;
          this.location = [];
          this.overlay = true;
        }

        if (values[2].status !== "rejected") {
          handleAlerts(values[2].value);
        } else {
          this.alerts = [];
          this.selectedAlerts = [];
          text.push("sensors");
        }

        let uniqueText = [...new Set(text)];

        switch (uniqueText.length) {
          case 0:
            break;
          case 1:
            snackbar.error(
              this.$t("utils.snackbar.error.insufficientRightsFor") +
                uniqueText.join(" ")
            );
            break;
          case 2:
            snackbar.error(
              this.$t("utils.snackbar.error.insufficientRightsFor") +
                uniqueText.join(" " + this.$t("data.data.and") + " ")
            );
            break;
          case 3:
            snackbar.error(this.$t("utils.snackbar.error.insufficientRights"));
            break;
        }

        this.loading = false;
        this.forceRefresh++;
      });
    },

    getSelectedSensorsFromLink() {
      return this.$route.query.sensors.length !== 0
        ? this.$route.query.sensors.split("%2C")
        : [];
    },

    handleRoute() {
      if (
        this.$route.query.datefrom &&
        this.$route.query.dateto &&
        typeof this.$route.query.id !== "undefined"
      ) {
        this.loadData(false);
      } else {
        this.$router.replace({ name: "data" });
      }
    },

    getSelectedSensors(event) {
      this.$set(this.selectedSensors, "selected", event.selected);
      this.$set(this.selectedSensors, "data", event.data);

      // this.selectedSensors = event;

      // this.forceUpdateSensorPicker++;
      if (event.selected.length !== 0) {
        this.$nextTick(() => {
          this.selectedAlerts = this.alerts.filter((element) =>
            event.selected.find((el) => el === element.device_sensor.uuid)
          );
        });
        this.$router.push({
          query: Object.assign({}, this.$route.query, {
            sensors: encodeURIComponent(event.selected),
          }),
        });
      } else {
        this.$nextTick(() => {
          this.selectedAlerts = this.alerts;
        });
      }
      this.forceRefreshCharts++;
    },

    loadAlerts(selectedSensor) {
      return api.getDeviceAlarms(
        this.deviceSerial,
        this.dateRange[0],
        this.dateRange[1],
        selectedSensor,
        false
      );
    },

    loadSensorList() {
      return api.getDeviceSensorsList(this.deviceSerial, false);
    },

    loadLocation() {
      return api.getDevicePosition(
        this.deviceSerial,
        this.dateRange[0],
        this.dateRange[1],
        false
      );
    },

    loadSensorData(sensorId, callback) {
      this.loadingCharts = true;
      api
        .fetchSensorData(
          this.deviceSerial,
          sensorId,
          this.dateRange[0],
          this.dateRange[1]
        )
        .then((resp) => {
          //GET USERS unitPreference FROM USERS/CRM/ME
          const unitPreference = this.getMe.user.profile.unit_preference;
          const measureType = resp.data.sensor.measure_type;

          resp.data.last_record_value = unitConverter.format(
            resp.data.last_record_value,
            measureType,
            unitPreference,
            null,
            null,
            false
          );

          resp.data.records = resp.data.records.map((record) => {
            return {
              ...record,
              value: unitConverter.getConvertedValue(
                record.value,
                measureType,
                unitPreference,
                null,
                resp.data.sensor
              ),
            };
          });

          this.loadingCharts = false;
          callback(resp);
          this.forceCharts++;
        })
        .catch((err) => {
          this.loadingCharts = false;
          let resp = { data: { records: [] } };
          callback(resp);
          this.forceCharts++;
        });
    },
  },
};
</script>

<style></style>
